import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, User } from '@auth0/auth0-angular';
import { Observable, Subscription } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { Menu } from 'primeng/menu';
import { NGXLogger } from 'ngx-logger';
import { UserRole, UserRoleService, UserRoleValue } from '../../../services/user-role.service';

const ROLE_CHIP_CLASSES: Record<Exclude<UserRole, null>, string> = {
  [UserRoleValue.Admin]: 'role-admin',
  [UserRoleValue.Referente]: 'role-referente',
  [UserRoleValue.Standard]: 'role-standard',
  [UserRoleValue.Base]: 'role-base',
};

@Component({
  selector: 'app-navbar',
  standalone: false,
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
})
export class NavbarComponent implements OnInit, OnDestroy {
  @ViewChild('userMenu') userMenu!: Menu;
  @ViewChild('mobileMenu') mobileMenu!: Menu;
  @ViewChild('configMenu') configMenu!: Menu;

  isAuthenticated$: Observable<boolean>;
  user$: Observable<User | null | undefined>;
  currentUser: User | null | undefined = null;
  userMenuItems: MenuItem[] = [];
  mobileMenuItems: MenuItem[] = [];
  configMenuItems: MenuItem[] = [];
  mobileMenuVisible = false;
  supportTicketDialogVisible = false;
  private subscriptions = new Subscription();

  constructor(
    private auth: AuthService,
    private router: Router,
    private logger: NGXLogger,
    private userRoleService: UserRoleService,
  ) {
    this.isAuthenticated$ = this.auth.isAuthenticated$;
    this.user$ = this.auth.user$;
  }

  ngOnInit() {
    this.initializeUserMenu();
    this.subscribeToUser();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private subscribeToUser() {
    this.subscriptions.add(
      this.user$.subscribe((user) => {
        this.currentUser = user;
      }),
    );

  }

  private initializeUserMenu() {
    this.userMenuItems = [
      {
        label: 'SUPPORTO',
        icon: 'pi pi-headphones',
        command: () => this.openSupportTicketDialog(),
      },
      {
        separator: true,
      },
      {
        label: 'LOGOUT',
        icon: 'pi pi-sign-out',
        command: () => this.logout(),
      },
    ];

    this.configMenuItems = [
      {
        label: 'CLIENTI',
        icon: 'pi pi-building',
        command: () => {
          this.router.navigate(['/clienti']);
        },
      },
      {
        label: 'COMMESSE',
        icon: 'pi pi-briefcase',
        command: () => {
          this.router.navigate(['/commesse']);
        },
      },
      {
        label: 'RUOLI',
        icon: 'pi pi-id-card',
        command: () => {
          this.router.navigate(['/ruoli-referenza']);
        },
      },
      {
        label: 'TAGS',
        icon: 'pi pi-tags',
        command: () => {
          this.router.navigate(['/tags']);
        },
      },
    ];

    this.mobileMenuItems = [
      {
        label: 'HOME',
        icon: 'pi pi-home',
        command: () => {
          this.router.navigate(['/']);
          this.closeMobileMenu();
        },
      },
      {
        label: 'CONFIGURAZIONE',
        icon: 'pi pi-cog',
        items: this.configMenuItems,
      },
      {
        separator: true,
      },
      {
        label: 'SUPPORTO',
        icon: 'pi pi-headphones',
        command: () => {
          this.openSupportTicketDialog();
          this.closeMobileMenu();
        },
      },
    ];
  }

  toggleMobileMenu(event: Event) {
    event.stopPropagation();
    this.mobileMenuVisible = !this.mobileMenuVisible;
    if (this.mobileMenuVisible) {
      this.mobileMenu.show(event);
    } else {
      this.mobileMenu.hide();
    }
  }

  closeMobileMenu() {
    this.mobileMenuVisible = false;
    this.mobileMenu.hide();
  }

  onUserMenuToggle(event: Event) {
    event.stopPropagation();
    this.userMenu.toggle(event);
  }

  login(): void {
    this.auth
      .loginWithRedirect({
        appState: { target: '/' },
      })
      .subscribe();
  }

  logout(): void {
    // Pulisci la sessione
    this.clearSession();
    // Esegui il logout di Auth0
    this.auth.logout({
      logoutParams: {
        returnTo: globalThis.location.origin,
        federated: true,
      },
    });
  }

  private clearSession(): void {
    // Pulisci completamente sessionStorage
    sessionStorage.clear();

    // Pulisci completamente localStorage
    localStorage.clear();
  }

  goToHome() {
    this.router.navigate(['/']);
  }

  openSupportTicketDialog() {
    this.supportTicketDialogVisible = true;
  }

  getInitials(name?: string): string {
    if (!name) return 'U';
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  }

  getRoleChipClass(): string {
    const role = this.userRoleService.role();
    return role ? ROLE_CHIP_CLASSES[role] : 'role-default';
  }

  getRoleDisplayName(): string {
    const role = this.userRoleService.role();
    return role ?? 'Utente';
  }
}
